import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { formatPrice } from '@/components/PropertyCard';
import { MapPin, Phone, MessageCircle, ShieldCheck, Check, Building2, Award } from 'lucide-react';

const FALLBACK_IMG = 'https://images.unsplash.com/photo-1649989184479-73ab3b750a73?crop=entropy&cs=srgb&fm=jpg&q=85';

const LandingPageView = () => {
  const { brokerSlug, projectSlug } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lead, setLead] = useState({ name: '', mobile: '', email: '', message: '' });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => { load(); }, [brokerSlug, projectSlug]);

  const load = async () => {
    try {
      const { data } = await api.get(`/public/landing-page/${brokerSlug}/${projectSlug}`);
      setData(data);
    } catch (e) {
      toast.error('Landing page not found');
    } finally { setLoading(false); }
  };

  const submitLead = async (e) => {
    e.preventDefault();
    if (!data) return;
    setSubmitting(true);
    try {
      // Get UTM params from URL
      const urlParams = new URLSearchParams(window.location.search);
      await api.post('/leads', {
        ...lead,
        broker_id: data.broker.user_id,
        project_id: data.project.id,
        landing_page_id: data.landing_page.id,
        source: 'landing_page',
        utm_source: urlParams.get('utm_source'),
        utm_medium: urlParams.get('utm_medium'),
        utm_campaign: urlParams.get('utm_campaign')
      });
      setSubmitted(true);
      toast.success('Thank you! Broker will contact you shortly.');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to submit');
    } finally { setSubmitting(false); }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center">Loading...</div>;
  if (!data) return <div className="min-h-screen flex items-center justify-center text-slate-500">Landing page not found</div>;

  const { landing_page: lp, project, broker } = data;

  return (
    <div className="min-h-screen bg-white">
      {/* HERO */}
      <section className="relative h-[500px] md:h-[600px] overflow-hidden">
        <img src={lp.banner_url || project.cover_image_url || FALLBACK_IMG} alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/40 to-black/80" />

        <div className="relative z-10 h-full flex flex-col justify-center px-6 md:px-16 max-w-7xl mx-auto">
          <div className="flex items-center gap-2 mb-4">
            {project.rera_number && <Badge className="bg-emerald-500 gap-1 hover:bg-emerald-500"><ShieldCheck size={12}/> RERA {project.rera_number}</Badge>}
            <Badge className="bg-[#F97316] hover:bg-[#F97316]">{project.project_status?.replace(/_/g, ' ')}</Badge>
          </div>
          <h1 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl text-white tracking-tighter">
            {lp.headline || project.name}
          </h1>
          {project.builder_name && <div className="mt-3 text-slate-200">by <span className="font-bold">{project.builder_name}</span></div>}
          <div className="flex items-center gap-1 text-slate-200 mt-2">
            <MapPin size={16} /> {project.locality}, {project.city}
          </div>
          <div className="mt-6 flex flex-wrap gap-3 items-center">
            <div className="bg-white/95 backdrop-blur-sm rounded-lg px-5 py-3">
              <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Starting from</div>
              <div className="font-display font-black text-2xl text-[#0F2C59]">{formatPrice(project.price_min)}</div>
            </div>
            <a href="#enquiry" className="bg-[#F97316] hover:bg-[#EA580C] text-white font-bold px-6 py-3 rounded-lg" data-testid="hero-cta">{lp.cta_text}</a>
          </div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* LEFT */}
        <div className="lg:col-span-2 space-y-6">
          {/* Config */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {[
              { label: 'Configurations', val: project.configurations?.join(', ') },
              { label: 'Unit Sizes', val: project.unit_sizes },
              { label: 'Possession', val: project.possession_date },
              { label: 'Total Units', val: project.total_units }
            ].map((s, i) => (
              <div key={i} className="bg-slate-50 p-4 rounded-lg">
                <div className="text-xs uppercase text-slate-500 font-semibold">{s.label}</div>
                <div className="font-bold text-slate-900 mt-1">{s.val || 'N/A'}</div>
              </div>
            ))}
          </div>

          {lp.show_gallery && project.gallery_images?.length > 0 && (
            <div>
              <h2 className="font-display font-bold text-2xl text-[#0F2C59] mb-3">Gallery</h2>
              <div className="grid grid-cols-2 gap-3">
                {project.gallery_images.map((img, i) => (
                  <img key={i} src={img} alt="" className="w-full h-48 object-cover rounded-lg" />
                ))}
              </div>
            </div>
          )}

          <div>
            <h2 className="font-display font-bold text-2xl text-[#0F2C59] mb-3">About the Project</h2>
            <p className="text-slate-600 leading-relaxed whitespace-pre-line">{project.overview}</p>
          </div>

          {project.highlights?.length > 0 && (
            <div>
              <h2 className="font-display font-bold text-2xl text-[#0F2C59] mb-3">Highlights</h2>
              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {project.highlights.map((h, i) => (
                  <li key={i} className="flex items-center gap-2 text-sm text-slate-700"><Check size={16} className="text-emerald-600"/> {h}</li>
                ))}
              </ul>
            </div>
          )}

          {lp.show_amenities && project.amenities?.length > 0 && (
            <div>
              <h2 className="font-display font-bold text-2xl text-[#0F2C59] mb-3">Amenities</h2>
              <div className="flex flex-wrap gap-2">
                {project.amenities.map((a, i) => (
                  <span key={i} className="px-3 py-1.5 bg-slate-100 text-slate-700 rounded-md text-sm">{a}</span>
                ))}
              </div>
            </div>
          )}

          {project.location_advantages?.length > 0 && (
            <div>
              <h2 className="font-display font-bold text-2xl text-[#0F2C59] mb-3">Location Advantages</h2>
              <ul className="space-y-2">
                {project.location_advantages.map((l, i) => (
                  <li key={i} className="flex items-center gap-2 text-sm text-slate-700"><MapPin size={14} className="text-[#F97316]"/> {l}</li>
                ))}
              </ul>
            </div>
          )}

          {/* Broker Profile */}
          {lp.show_broker_profile && broker && (
            <Link to={`/broker/${broker.slug}`} className="block bg-gradient-to-br from-[#0F2C59] to-[#1E3A6F] text-white rounded-lg p-6 hover:shadow-xl transition-shadow" data-testid="broker-profile-link">
              <div className="flex items-start gap-4">
                <div className="w-16 h-16 rounded-full bg-white text-[#0F2C59] flex items-center justify-center font-black text-2xl">
                  {broker.full_name?.charAt(0)?.toUpperCase()}
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <h3 className="font-display font-bold text-lg">{broker.full_name}</h3>
                    <Badge className="bg-emerald-500 hover:bg-emerald-500 gap-1 text-xs"><ShieldCheck size={10}/> Verified</Badge>
                  </div>
                  {broker.agency_name && <div className="text-slate-200 text-sm">{broker.agency_name}</div>}
                  <div className="flex items-center gap-3 mt-2 text-xs text-slate-300">
                    <span><Award size={12} className="inline mr-1"/>{broker.years_of_experience} yrs exp</span>
                    <span><MapPin size={12} className="inline mr-1"/>{broker.primary_city}</span>
                  </div>
                  {broker.bio && <p className="mt-3 text-sm text-slate-200 line-clamp-3">{broker.bio}</p>}
                  <div className="mt-3 text-xs font-semibold text-[#F97316] inline-flex items-center gap-1">View Full Profile →</div>
                </div>
              </div>
            </Link>
          )}
        </div>

        {/* RIGHT - Enquiry Form (sticky) */}
        <div id="enquiry" className="lg:col-span-1">
          <div className="bg-white rounded-lg border-2 border-[#F97316] p-6 sticky top-4 shadow-lg">
            {submitted ? (
              <div className="text-center py-8">
                <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-3">
                  <Check size={30} className="text-emerald-600"/>
                </div>
                <h3 className="font-display font-bold text-xl text-[#0F2C59]">Thank You!</h3>
                <p className="text-sm text-slate-600 mt-2">Our verified broker will contact you shortly.</p>
                {broker && (
                  <div className="mt-4 p-3 bg-slate-50 rounded-md text-left">
                    <div className="text-xs text-slate-500 uppercase font-semibold mb-1">Your Broker</div>
                    <div className="font-semibold text-sm">{broker.full_name}</div>
                    {broker.whatsapp_number && <a href={`https://wa.me/${broker.whatsapp_number.replace(/\D/g, '')}`} className="inline-flex items-center gap-1 mt-2 text-emerald-600 text-sm font-semibold"><MessageCircle size={14}/> WhatsApp</a>}
                  </div>
                )}
              </div>
            ) : (
              <>
                <h3 className="font-display font-bold text-xl text-[#0F2C59]">{lp.cta_text}</h3>
                <p className="text-xs text-slate-500 mt-1">Get instant callback within 15 mins</p>
                <form onSubmit={submitLead} className="mt-4 space-y-3">
                  <div>
                    <Label>Full Name *</Label>
                    <Input value={lead.name} onChange={(e) => setLead({...lead, name: e.target.value})} required data-testid="lp-name-input" />
                  </div>
                  <div>
                    <Label>Mobile *</Label>
                    <Input type="tel" value={lead.mobile} onChange={(e) => setLead({...lead, mobile: e.target.value})} required data-testid="lp-mobile-input" />
                  </div>
                  <div>
                    <Label>Email</Label>
                    <Input type="email" value={lead.email} onChange={(e) => setLead({...lead, email: e.target.value})} data-testid="lp-email-input" />
                  </div>
                  <div>
                    <Label>Message</Label>
                    <Textarea value={lead.message} onChange={(e) => setLead({...lead, message: e.target.value})} rows={3} placeholder="I'm interested in..." data-testid="lp-message-input" />
                  </div>
                  <Button type="submit" disabled={submitting} className="w-full bg-[#F97316] hover:bg-[#EA580C] font-bold h-11" data-testid="lp-submit-btn">
                    {submitting ? 'Sending...' : lp.cta_text}
                  </Button>
                </form>
                <div className="mt-4 pt-4 border-t border-slate-200 text-xs text-slate-500 text-center flex items-center justify-center gap-1">
                  <ShieldCheck size={12} className="text-emerald-600"/> Your data is 100% secure
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="border-t border-slate-200 py-6 text-center text-xs text-slate-500">
        Powered by <span className="font-semibold text-[#0F2C59]">PropertyByKilo</span> — India's Premium PropTech Platform
      </footer>

      {/* Sticky Mobile CTA */}
      <div className="fixed bottom-0 left-0 right-0 lg:hidden bg-white border-t border-slate-200 p-3 z-50">
        <a href="#enquiry" className="block w-full bg-[#F97316] hover:bg-[#EA580C] text-white font-bold text-center py-3 rounded-lg">{lp.cta_text}</a>
      </div>
    </div>
  );
};

export default LandingPageView;
